<template>
  <div class="date-period">
    <button
      type="button"
      class="date-period__nav"
      @click="backPeriod"
      :aria-label="$t('components.fields.to')"
    >
      <i class="fas fa-chevron-left" aria-hidden="true"></i>
    </button>

    <div class="date-period__label">
      <template v-if="isWeek">
        <formatted-date-component :val="start" :without-year="true" />
        <span class="date-period__to">{{ $t("components.fields.to") }}</span>
        <formatted-date-component :val="end" :without-year="true" />
      </template>

      <template v-else-if="isMonth">
        <span class="month">{{
          $t(`components.date.month_long[${start.getMonth()}]`) + ` ${start.getFullYear()}`
        }}</span>
      </template>

      <template v-else>
        <span>{{ $formatDate(this.start) }}</span>
      </template>
    </div>

    <button type="button" class="date-period__nav" @click="nextPeriod" aria-label="next">
      <i class="fas fa-chevron-right" aria-hidden="true"></i>
    </button>
  </div>
</template>

<script>
import { addMonths, endOfWeek, startOfWeek, addDays, startOfMonth, endOfMonth } from "date-fns";

import { FormattedDateComponent } from "@/components/formatted";
import { SUC_DATE_PERIOD_CHANGED_EVENT, SUC_DATE_PERIOD_SET_DATE_EVENT } from "./index";

import {
  DATE_PERIOD_WEEK,
  DATE_PERIOD_MONTH,
  DATE_PERIOD_DAY,
} from "@/components/form/data/periods";

export default {
  name: "SucDatePeriodComponent",
  components: {
    FormattedDateComponent,
  },
  props: {
    type: {
      type: String,
      validator: function (val) {
        const types = [DATE_PERIOD_DAY, DATE_PERIOD_WEEK, DATE_PERIOD_MONTH];
        if (!types.includes(val)) {
          console.error("type shoud be one of", types);
          return false;
        }
        return true;
      },
      default: DATE_PERIOD_WEEK,
    },
  },
  data() {
    return {
      initialDate: null, //use this date to set custom period by global event
      start: null,
      end: null,
      options: { weekStartsOn: 1 },
    };
  },
  computed: {
    isWeek() {
      return this.type === DATE_PERIOD_WEEK;
    },
    isMonth() {
      return this.type === DATE_PERIOD_MONTH;
    },
    isDay() {
      return this.type === DATE_PERIOD_DAY;
    },
    initDate() {
      return this.$isset(this.initialDate) ? this.initialDate : new Date();
    },
  },
  watch: {
    type() {
      this.currentPeriod();
    },
  },
  created() {
    this.currentPeriod();
    EventBus.listen(SUC_DATE_PERIOD_SET_DATE_EVENT, ({ date }) => {
      this.initialDate = date;
      this.currentPeriod();
    });
  },
  beforeDestroy() {
    EventBus.off(SUC_DATE_PERIOD_SET_DATE_EVENT);
  },
  methods: {
    currentPeriod() {
      if (this.isWeek) {
        this._currentWeek();
      } else if (this.isMonth) {
        this._currentMonth();
      } else {
        this._currentDay();
      }
      this._fireEvent();
      this.initialDate = null;
    },

    nextPeriod() {
      if (this.isWeek) {
        this._nextWeek();
      } else if (this.isMonth) {
        this._nextMonth();
      } else {
        this._nextDay();
      }
      this._fireEvent();
    },

    backPeriod() {
      if (this.isWeek) {
        this._lastWeek();
      } else if (this.isMonth) {
        this._lastMonth();
      } else {
        this._lastDay();
      }
      this._fireEvent();
    },
    _fireEvent() {
      this.$emit(SUC_DATE_PERIOD_CHANGED_EVENT, {
        start: this.start,
        end: this.end,
        type: this.type,
      });
    },
    _currentDay() {
      this.start = this.end = this.initDate;
    },
    _nextDay() {
      this.start = this.end = addDays(this.start, 1);
    },
    _lastDay() {
      this.start = this.end = addDays(this.start, -1);
    },
    _currentWeek() {
      this.start = startOfWeek(this.initDate, this.options);
      this.end = endOfWeek(this.initDate, this.options);
    },
    _nextWeek() {
      this.start = addDays(this.start, 7);
      this.end = endOfWeek(this.start, this.options);
    },
    _lastWeek() {
      this.start = addDays(this.start, -7);
      this.end = endOfWeek(this.start, this.options);
    },
    _currentMonth() {
      this.start = startOfMonth(this.initDate);
      this.end = endOfMonth(this.initDate);
    },
    _nextMonth() {
      this.start = addMonths(this.start, 1);
      this.end = endOfMonth(this.start);
    },
    _lastMonth() {
      this.start = addMonths(this.start, -1);
      this.end = endOfMonth(this.start);
    },
  },
};
</script>

<style lang="scss" scoped>
.date-period {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.25rem;
  border: 1px solid var(--portal-border);
  border-radius: 12px;
  background: var(--portal-surface);
}

.date-period__nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--portal-text-muted);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: var(--portal-primary-soft);
    color: var(--portal-primary);
  }

  &:focus {
    outline: 2px solid var(--portal-ring);
    outline-offset: 1px;
  }

  i {
    font-size: 0.78rem;
  }
}

.date-period__label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  padding: 0 0.35rem;
  color: var(--portal-text);
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.date-period__to {
  color: var(--portal-text-muted);
  font-weight: 500;
}

.month {
  text-transform: capitalize;
}
</style>
